import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  doc,
  getDoc,
  query,
  where,
  getDocs,
  collection,
} from "firebase/firestore";
import { db } from "../../db/firebase";
import { Link } from "react-router-dom";
import VideoCarousel from "../../components/VideoCarousel";
import {
  HiOutlineCalendarDays,
  HiOutlineClock,
  HiOutlineMusicalNote,
  HiOutlineBookOpen,
  HiOutlineArrowRight,
  HiOutlineSparkles,
} from "react-icons/hi2";
import { LoadingMusicIcon } from "../../components/Loading";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import moment from "moment";
import TopSongs from "../Organization/Events/TopSongs";
import { getVideoCarouselItemsFromSong } from "../../utils/youtubeLinks";
import { useAuth } from "../../context/AuthContext";

function songIdsFromEvent(event: Record<string, any>): string[] {
  const ids = new Set<string>();
  const push = (songs: unknown) => {
    if (!Array.isArray(songs)) return;
    songs.forEach((s) => {
      if (typeof s === "string" && s.trim()) ids.add(s.trim());
      else if (s && typeof s === "object" && "id" in s) {
        const id = String((s as { id: string }).id).trim();
        if (id) ids.add(id);
      }
    });
  };
  push(event.songs);
  if (Array.isArray(event.serviceOrder)) {
    event.serviceOrder.forEach((item: any) => {
      if (item && typeof item === "object") push(item.songs);
    });
  }
  return Array.from(ids);
}

function eventDateTime(event: { date?: string; time?: string }): Date {
  return new Date(`${event.date || ""}T${event.time || "23:59"}`);
}

// Map i18n language codes to bible-api.com translation identifiers
// bible-api.com supports: web, kjv, asv, bbe, darby, dra, ylt, almeida (pt), bkr (cs), cuv (zh), synodal (ru), rccv (ro), clementine (la)
const BIBLE_TRANSLATIONS: Record<string, string> = {
  en: "web",
  es: "es", // Use bible-api.deno.dev (Reina Valera 1960) for Spanish
  pt: "almeida",
  "pt-BR": "almeida",
  cs: "bkr",
  zh: "cuv",
  ru: "synodal",
  ro: "rccv",
};

// Map English book names (Discovery API format) to bible-api.deno.dev Spanish slugs
const EN_TO_ES_BOOK: Record<string, string> = {
  Genesis: "genesis",
  Exodus: "exodo",
  Leviticus: "levitico",
  Numbers: "numeros",
  Deuteronomy: "deuteronomio",
  Joshua: "josue",
  Judges: "jueces",
  Ruth: "rut",
  "1 Samuel": "1-samuel",
  "2 Samuel": "2-samuel",
  "1 Kings": "1-reyes",
  "2 Kings": "2-reyes",
  "1 Chronicles": "1-cronicas",
  "2 Chronicles": "2-cronicas",
  Ezra: "esdras",
  Nehemiah: "nehemias",
  Esther: "ester",
  Job: "job",
  Psalms: "salmos",
  Proverbs: "proverbios",
  Ecclesiastes: "eclesiastes",
  "Song of Solomon": "cantares",
  "Song of Songs": "cantares",
  Isaiah: "isaias",
  Jeremiah: "jeremias",
  Lamentations: "lamentaciones",
  Ezekiel: "ezequiel",
  Daniel: "daniel",
  Hosea: "oseas",
  Joel: "joel",
  Amos: "amos",
  Obadiah: "abdias",
  Jonah: "jonas",
  Micah: "miqueas",
  Nahum: "nahum",
  Habakkuk: "habacuc",
  Zephaniah: "sofonias",
  Haggai: "hageo",
  Zechariah: "zacarias",
  Malachi: "malaquias",
  Matthew: "mateo",
  Mark: "marcos",
  Luke: "lucas",
  John: "juan",
  Acts: "hechos",
  Romans: "romanos",
  "1 Corinthians": "1-corintios",
  "2 Corinthians": "2-corintios",
  Galatians: "galatas",
  Ephesians: "efesios",
  Philippians: "filipenses",
  Colossians: "colosenses",
  "1 Thessalonians": "1-tesalonicenses",
  "2 Thessalonians": "2-tesalonicenses",
  "1 Timothy": "1-timoteo",
  "2 Timothy": "2-timoteo",
  Titus: "tito",
  Philemon: "filemon",
  Hebrews: "hebreos",
  James: "santiago",
  "1 Peter": "1-pedro",
  "2 Peter": "2-pedro",
  "1 John": "1-juan",
  "2 John": "2-juan",
  "3 John": "3-juan",
  Jude: "judas",
  Revelation: "apocalipsis",
};

// Spanish reference display names (for cite)
const ES_BOOK_DISPLAY: Record<string, string> = {
  genesis: "Génesis",
  exodo: "Éxodo",
  levitico: "Levítico",
  numeros: "Números",
  deuteronomio: "Deuteronomio",
  josue: "Josué",
  jueces: "Jueces",
  rut: "Rut",
  "1-samuel": "1 Samuel",
  "2-samuel": "2 Samuel",
  "1-reyes": "1 Reyes",
  "2-reyes": "2 Reyes",
  "1-cronicas": "1 Crónicas",
  "2-cronicas": "2 Crónicas",
  esdras: "Esdras",
  nehemias: "Nehemías",
  ester: "Ester",
  job: "Job",
  salmos: "Salmos",
  proverbios: "Proverbios",
  eclesiastes: "Eclesiastés",
  cantares: "Cantares",
  isaias: "Isaías",
  jeremias: "Jeremías",
  lamentaciones: "Lamentaciones",
  ezequiel: "Ezequiel",
  daniel: "Daniel",
  oseas: "Oseas",
  joel: "Joel",
  amos: "Amós",
  abdias: "Abdías",
  jonas: "Jonás",
  miqueas: "Miqueas",
  nahum: "Nahúm",
  habacuc: "Habacuc",
  sofonias: "Sofonías",
  hageo: "Hageo",
  zacarias: "Zacarías",
  malaquias: "Malaquías",
  mateo: "Mateo",
  marcos: "Marcos",
  lucas: "Lucas",
  juan: "Juan",
  hechos: "Hechos",
  romanos: "Romanos",
  "1-corintios": "1 Corintios",
  "2-corintios": "2 Corintios",
  galatas: "Gálatas",
  efesios: "Efesios",
  filipenses: "Filipenses",
  colosenses: "Colosenses",
  "1-tesalonicenses": "1 Tesalonicenses",
  "2-tesalonicenses": "2 Tesalonicenses",
  "1-timoteo": "1 Timoteo",
  "2-timoteo": "2 Timoteo",
  tito: "Tito",
  filemon: "Filemón",
  hebreos: "Hebreos",
  santiago: "Santiago",
  "1-pedro": "1 Pedro",
  "2-pedro": "2 Pedro",
  "1-juan": "1 Juan",
  "2-juan": "2 Juan",
  "3-juan": "3 Juan",
  judas: "Judas",
  apocalipsis: "Apocalipsis",
};

const UserDashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user, organizationId, profile } = useAuth();
  const [firstName, setFirstName] = useState("");
  const [events, setEvents] = useState<any[]>([]);
  const [songsData, setSongsData] = useState<any[]>([]);
  const [playlistEventTitle, setPlaylistEventTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [dailyVerse, setDailyVerse] = useState<{
    reference: string;
    text: string;
  } | null>(null);
  const [dailyVerseLoading, setDailyVerseLoading] = useState(true);

  useEffect(() => {
    const fetchDailyVerse = async () => {
      setDailyVerseLoading(true);
      const lang = (i18n.language || "en").split("-")[0];
      const translation =
        BIBLE_TRANSLATIONS[lang] || BIBLE_TRANSLATIONS[i18n.language] || "web";

      try {
        // 1. Get verse of the day reference from Discovery (English)
        const discoveryRes = await fetch(
          "https://discoverybiblestudy.org/daily/api/",
        );
        const discoveryData = await discoveryRes.json();
        const ref = discoveryData?.ref
          ?.replace(/\s+/g, " ")
          .replace(/\s*:\s*/g, ":")
          .trim();
        const enText = discoveryData?.text?.replace(/&quot;/g, '"').trim();

        if (!ref) throw new Error("No reference");

        // 2. For Spanish: use bible-api.deno.dev (Reina Valera 1960)
        if (translation === "es") {
          const match = ref.match(/^(.+?)\s+(\d+):(\d+)$/);
          if (match) {
            const [, bookEn, chapter, verseNum] = match;
            const bookSlug = EN_TO_ES_BOOK[bookEn.trim()];
            if (bookSlug) {
              const esRes = await fetch(
                `https://bible-api.deno.dev/api/read/rv1960/${bookSlug}/${chapter}`,
              );
              const esData = await esRes.json();
              const vers = esData?.vers as
                | Array<{ verse: string; number: number }>
                | undefined;
              const v = vers?.find((x) => x.number === parseInt(verseNum, 10));
              if (v?.verse) {
                const displayBook = ES_BOOK_DISPLAY[bookSlug] || bookEn;
                setDailyVerse({
                  reference: `${displayBook} ${chapter}:${verseNum}`,
                  text: v.verse.trim().replace(/\n+/g, " "),
                });
                setDailyVerseLoading(false);
                return;
              }
            }
          }
          // Fallback to Discovery English if Spanish fetch fails
          if (enText) {
            setDailyVerse({ reference: ref, text: enText });
          }
          setDailyVerseLoading(false);
          return;
        }

        // 3. For other languages: bible-api.com (CORS-friendly)
        const passage = encodeURIComponent(ref);
        const bibleRes = await fetch(
          `https://bible-api.com/${passage}?translation=${translation}`,
        );
        const bibleData = await bibleRes.json();

        if (bibleData?.text) {
          setDailyVerse({
            reference: bibleData.reference || ref,
            text: (bibleData.text || "").trim().replace(/\n+/g, " "),
          });
        } else if (enText) {
          setDailyVerse({ reference: ref, text: enText });
        }
      } catch {
        try {
          const fallback = await fetch(
            "https://labs.bible.org/api/?passage=votd&type=json",
          );
          const arr = await fallback.json();
          if (Array.isArray(arr) && arr.length > 0) {
            const v = arr[0];
            setDailyVerse({
              reference: `${v.bookname} ${v.chapter}:${v.verse}`,
              text: (v.text || "").trim(),
            });
          }
        } catch {
          // Silently fail - verse is optional
        }
      } finally {
        setDailyVerseLoading(false);
      }
    };
    fetchDailyVerse();
  }, [i18n.language]);

  useEffect(() => {
    setFirstName(profile?.firstName || "");
  }, [profile]);

  const fetchDashboardData = useCallback(async () => {
    if (!user || !organizationId) {
      setEvents([]);
      setSongsData([]);
      setPlaylistEventTitle("");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const eventsQuery = query(
        collection(db, "events"),
        where("organizationId", "==", organizationId),
        where("status", "==", "Active"),
      );

      const eventsSnapshot = await getDocs(eventsQuery);
      const now = Date.now();
      const fourteenDaysMs = 14 * 24 * 60 * 60 * 1000;

      const allRecentEvents = eventsSnapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((event: any) => {
          const when = eventDateTime(event).getTime();
          if (Number.isNaN(when)) return false;
          // Upcoming always; past only within last 14 days
          return when >= now || now - when <= fourteenDaysMs;
        });

      // Show the page as soon as events are ready — don't block on playlist song docs
      setEvents(allRecentEvents);
      setLoading(false);

      // Weekly Playlist: YouTube videos from next/latest event with songs
      const withSongs = allRecentEvents
        .map((event) => ({ event, songIds: songIdsFromEvent(event) }))
        .filter((x) => x.songIds.length > 0);

      const upcoming = withSongs
        .filter((x) => eventDateTime(x.event).getTime() >= now)
        .sort(
          (a, b) =>
            eventDateTime(a.event).getTime() - eventDateTime(b.event).getTime(),
        );
      const past = withSongs
        .filter((x) => eventDateTime(x.event).getTime() < now)
        .sort(
          (a, b) =>
            eventDateTime(b.event).getTime() - eventDateTime(a.event).getTime(),
        );

      const pick = upcoming[0] || past[0];
      if (!pick) {
        setSongsData([]);
        setPlaylistEventTitle("");
        return;
      }

      setPlaylistEventTitle(pick.event.title || "");

      const songsDataNested = await Promise.all(
        pick.songIds.map(async (songId) => {
          const songDoc = await getDoc(doc(db, "songs", songId));
          if (!songDoc.exists()) return [];
          const data = songDoc.data();
          return getVideoCarouselItemsFromSong({
            title: data.title,
            artist: data.artist,
            ...data,
          });
        }),
      );

      setSongsData(songsDataNested.flat() as any[]);
    } catch (err) {
      console.error(err);
      setEvents([]);
      setSongsData([]);
      setPlaylistEventTitle("");
      setLoading(false);
    }
  }, [user, organizationId]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const sortedEvents = useMemo(() => {
    const now = Date.now();
    return [...events].sort((a, b) => {
      const aTime = eventDateTime(a).getTime();
      const bTime = eventDateTime(b).getTime();
      const aPast = aTime < now;
      const bPast = bTime < now;
      if (aPast !== bPast) return aPast ? 1 : -1;
      return aPast ? bTime - aTime : aTime - bTime;
    });
  }, [events]);

  const upcomingCount = useMemo(() => {
    const now = Date.now();
    return events.filter((e) => eventDateTime(e).getTime() >= now).length;
  }, [events]);

  if (loading) {
    return (
      <div className="flex flex-col space-y-5 sm:space-y-6 animate-pulse">
        <div className="space-y-2">
          <div className="h-4 w-24 rounded-lg bg-gray-200 dark:bg-gray-700" />
          <div className="h-8 w-56 rounded-lg bg-gray-200 dark:bg-gray-700" />
          <div className="h-4 w-72 rounded-lg bg-gray-200 dark:bg-gray-700" />
        </div>
        <div className="h-36 rounded-2xl bg-gray-200 dark:bg-gray-700" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="h-72 rounded-2xl bg-gray-200 dark:bg-gray-700" />
          <div className="h-72 rounded-2xl bg-gray-200 dark:bg-gray-700" />
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className="flex flex-col min-h-0 space-y-5 sm:space-y-6"
    >
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#5932EA]/25 bg-[#5932EA]/10 text-[#4c2bd4] dark:text-[#c4b5fd] text-xs font-medium mb-3 backdrop-blur-sm">
          {t("home") || "Home"}
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white tracking-tight">
          {t("hello")}
          {firstName ? `, ${firstName}` : ""}
        </h1>
        <p className="mt-1.5 text-sm sm:text-base text-gray-500 dark:text-gray-400 max-w-xl">
          {t("memberHomeSubtitle") ||
            "Here's what's on for your team today"}
        </p>
      </div>

      {(dailyVerseLoading || dailyVerse) && (
        <div className="rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl border border-[#5932EA]/25 bg-[#5932EA]/10 text-[#5932EA] shrink-0">
              <HiOutlineBookOpen className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#5932EA]">
                {t("verseOfTheDay") || "Verse of the Day"}
              </p>
              {dailyVerseLoading ? (
                <div className="flex items-center gap-3 mt-3">
                  <LoadingMusicIcon size="md" />
                  <p className="text-sm text-gray-500 dark:text-gray-400 animate-pulse">
                    {t("loading")}
                  </p>
                </div>
              ) : dailyVerse ? (
                <>
                  <blockquote className="mt-2 text-sm sm:text-base text-gray-700 dark:text-gray-200 leading-relaxed">
                    &ldquo;{dailyVerse.text}&rdquo;
                  </blockquote>
                  <cite className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 not-italic mt-2 block">
                    — {dailyVerse.reference}
                  </cite>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/60 dark:bg-gray-800/40 backdrop-blur-sm p-4">
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs font-medium mb-1.5">
            <HiOutlineCalendarDays className="w-4 h-4 text-[#5932EA]" />
            {t("yourEvents")}
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">
            {events.length}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {upcomingCount > 0
              ? t("upcomingEventsCount", { count: upcomingCount }) ||
                `${upcomingCount} upcoming`
              : t("noUpcomingEvents") || "No upcoming"}
          </p>
        </div>
        <div className="rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/60 dark:bg-gray-800/40 backdrop-blur-sm p-4">
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs font-medium mb-1.5">
            <HiOutlineSparkles className="w-4 h-4 text-[#5932EA]" />
            {t("weeklyPlaylist")}
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">
            {songsData.length}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {t("practiceVideos") || "Practice videos"}
          </p>
        </div>
        <Link
          to="/songs"
          className="col-span-2 sm:col-span-1 group flex items-center gap-3 rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/60 dark:bg-gray-800/40 backdrop-blur-sm p-4 hover:border-[#5932EA]/30 hover:bg-[#5932EA]/5 dark:hover:bg-[#5932EA]/10 transition-colors"
        >
          <div className="flex items-center justify-center w-11 h-11 rounded-xl border border-[#5932EA]/25 bg-[#5932EA]/10 text-[#5932EA] shrink-0">
            <HiOutlineMusicalNote className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-gray-800 dark:text-gray-100">
              {t("songs")}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t("browseSongsLibrary") || "Browse library & chords"}
            </p>
          </div>
          <HiOutlineArrowRight className="w-4 h-4 text-gray-400 group-hover:text-[#5932EA] group-hover:translate-x-0.5 transition-all shrink-0" />
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
        {/* Weekly Playlist first — YouTube carousel from org setlist */}
        <section className="rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm overflow-hidden flex flex-col lg:order-1">
          <div className="px-4 sm:px-5 py-3.5 sm:py-4 border-b border-gray-100/80 dark:border-gray-700/80 flex items-center gap-2.5">
            <div className="flex items-center justify-center w-9 h-9 rounded-xl border border-[#5932EA]/25 bg-[#5932EA]/10 text-[#5932EA] shrink-0">
              <HiOutlineMusicalNote className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">
                {t("weeklyPlaylist")}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {playlistEventTitle
                  ? t("weeklyPlaylistFromEvent", {
                      title: playlistEventTitle,
                    }) || `From ${playlistEventTitle}`
                  : t("weeklyPlaylistHint") ||
                    "YouTube videos from your next (or latest) event setlist"}
              </p>
            </div>
          </div>
          <div className="p-3 sm:p-5 flex-1">
            {songsData.length > 0 ? (
              <VideoCarousel songsData={songsData} />
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center h-full min-h-[220px]">
                <div className="w-14 h-14 mb-3 rounded-2xl bg-[#5932EA]/10 text-[#5932EA] flex items-center justify-center">
                  <HiOutlineMusicalNote className="w-7 h-7" />
                </div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t("noPlaylistYet") || "No practice videos yet"}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-xs">
                  {t("noPlaylistYetHint") ||
                    "When an event has songs with YouTube links, they’ll show up here."}
                </p>
              </div>
            )}
          </div>
        </section>

        <section className="rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm overflow-hidden flex flex-col lg:order-2">
          <div className="px-4 sm:px-5 py-3.5 sm:py-4 border-b border-gray-100/80 dark:border-gray-700/80 flex items-center gap-2.5">
            <div className="flex items-center justify-center w-9 h-9 rounded-xl border border-[#5932EA]/25 bg-[#5932EA]/10 text-[#5932EA] shrink-0">
              <HiOutlineCalendarDays className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">
                {t("yourEvents")}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {t("yourEventsHint") || "Upcoming and recent active events"}
              </p>
            </div>
          </div>
          <div className="flex-1">
            {sortedEvents.length > 0 ? (
              <ul className="divide-y divide-gray-100/90 dark:divide-gray-700/70">
                {sortedEvents.map((event) => {
                  const when = eventDateTime(event);
                  const isPastEvent =
                    !Number.isNaN(when.getTime()) && when.getTime() < Date.now();
                  return (
                    <li key={event.id}>
                      <Link
                        to={`/my-events/${event.id}`}
                        className="group flex items-center gap-3 px-4 sm:px-5 py-3.5 hover:bg-[#5932EA]/5 dark:hover:bg-[#5932EA]/10 active:bg-[#5932EA]/8 transition-colors"
                      >
                        <div className="w-12 h-12 rounded-xl bg-[#5932EA]/10 text-[#5932EA] flex flex-col items-center justify-center shrink-0 leading-none">
                          <span className="text-[10px] font-semibold uppercase tracking-wide opacity-80">
                            {moment.utc(event.date).format("MMM")}
                          </span>
                          <span className="text-base font-bold tabular-nums mt-0.5">
                            {moment.utc(event.date).format("D")}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <h3 className="font-semibold text-sm text-gray-800 dark:text-gray-100 group-hover:text-[#5932EA] transition-colors truncate">
                              {event.title || t("untitledEvent") || "Untitled"}
                            </h3>
                            <span
                              className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide ${
                                isPastEvent
                                  ? "bg-amber-500/10 text-amber-700 dark:text-amber-300"
                                  : "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                              }`}
                            >
                              {isPastEvent
                                ? t("passedEvent") || "Past"
                                : t("upcoming") || "Upcoming"}
                            </span>
                          </div>
                          <div className="mt-1 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 truncate">
                            <span className="inline-flex items-center gap-1 shrink-0">
                              <HiOutlineCalendarDays className="w-3.5 h-3.5 text-[#5932EA]/70" />
                              {moment.utc(event.date).format("MMM D")}
                            </span>
                            {event.time && (
                              <>
                                <span className="text-gray-300 dark:text-gray-600" aria-hidden>
                                  ·
                                </span>
                                <span className="inline-flex items-center gap-1 shrink-0">
                                  <HiOutlineClock className="w-3.5 h-3.5 text-[#5932EA]/70" />
                                  {event.time}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <HiOutlineArrowRight className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-[#5932EA] group-hover:translate-x-0.5 transition-all shrink-0" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                <div className="w-14 h-14 mb-3 rounded-2xl bg-[#5932EA]/10 text-[#5932EA] flex items-center justify-center">
                  <HiOutlineCalendarDays className="w-7 h-7" />
                </div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t("noEventsFound") || "No events found"}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-xs">
                  {t("noEventsDescription") ||
                    "Active events will appear here"}
                </p>
              </div>
            )}
          </div>
        </section>
      </div>

      <TopSongs />
    </motion.div>
  );
};

export default UserDashboard;
